import { astarInterface } from '../astar/astarInterface-sidecar'

export const TypeSpace = 0
export const TypeWall = 1
export const TypeCorridor = 2
export const TypeLadder = 3
export const TypeAirlock = 4
export const TypeSpaceWithShip = 5 // Extra cost

export const LevelSpace = 0
export const LevelShip = 1

export interface IPathNode {
  i: number
  j: number
  k: number // In ship or no?

  type: number
}

export interface IPathGrid {
  width: number
  height: number
  nodes: IPathNode[]
}
export function isGridSpotInRange(
  grid: IPathGrid,
  i: number,
  j: number,
  k: number,
) {
  if (i < 0 || i >= grid.width) {
    return false
  }
  if (j < 0 || j >= grid.height) {
    return false
  }
  if (k < 0 || k >= 2) {
    return false
  }
  return true
}
export function getPathGridSpot(
  grid: IPathGrid,
  i: number,
  j: number,
  k: number,
) {
  if (!isGridSpotInRange(grid, i, j, k)) {
    return false
  }
  let idx = i + j * grid.width + k * (grid.width * grid.height)

  return grid.nodes[idx]
}
export function getPathGridSpotUnsafe(
  grid: IPathGrid,
  i: number,
  j: number,
  k: number,
) {
  if (!isGridSpotInRange(grid, i, j, k)) {
    throw new Error(`invalid grid spot ${i},${j},${k} `)
  }
  let idx = i + j * grid.width + k * (grid.width * grid.height)
  return grid.nodes[idx]
}

const hash = (node: IPathNode) => {
  return node.i + ':' + node.j + ':' + node.k
}

export const findPath = (
  grid: IPathGrid,
  start: IPathNode,
  end: IPathNode,
  allowNextTo: boolean,
) => {
  const distance = (a: IPathNode, b: IPathNode) => {
    let dx = Math.abs(a.i - b.i)
    let dy = Math.abs(a.j - b.j)
    let dz = Math.abs(a.k - b.k)
    let h = dx + dy + dz * 100
    if (a.type === TypeSpaceWithShip || b.type === TypeSpaceWithShip) {
      h += 50
    }
    // console.log('d', h)
    return h
  }
  const heuristic = (node: IPathNode) => {
    let dx = Math.abs(node.i - end.i)
    let dy = Math.abs(node.j - end.j)
    let dz = Math.abs(node.k - end.k)
    let h = dx + dy + dz * 100
    // console.log('h', h)
    return h
  }
  const neighbor = (node: IPathNode) => {
    // left, right, up, down, traverse plane
    if (node.type === TypeWall) {
      return []
    }
    let neighbors: IPathNode[] = []

    let tl = getPathGridSpot(grid, node.i - 1, node.j + 0, node.k)
    if (
      tl &&
      tl.type !== TypeWall &&
      !(node.k === LevelShip && tl.type === TypeSpace)
    ) {
      neighbors.push(tl)
    }
    let tr = getPathGridSpot(grid, node.i + 1, node.j + 0, node.k)
    if (
      tr &&
      tr.type !== TypeWall &&
      !(node.k === LevelShip && tr.type === TypeSpace)
    ) {
      neighbors.push(tr)
    }
    let tt = getPathGridSpot(grid, node.i, node.j - 1, node.k)
    if (
      tt &&
      tt.type !== TypeWall &&
      !(node.k === LevelShip && tt.type === TypeSpace)
    ) {
      neighbors.push(tt)
    }

    let tb = getPathGridSpot(grid, node.i, node.j + 1, node.k)
    if (
      tb &&
      tb.type !== TypeWall &&
      !(node.k === LevelShip && tb.type === TypeSpace)
    ) {
      neighbors.push(tb)
    }

    if (node.type === TypeAirlock) {
      let tv = getPathGridSpot(
        grid,
        node.i + 1,
        node.j + 0,
        node.k === LevelSpace ? LevelShip : LevelSpace,
      )
      if (tv) {
        neighbors.push(tv)
      }
    }

    // console.log('nb', node, neighbors)
    return neighbors
  }

  const isEnd = (node: IPathNode) => {
    // TODO: next to / below
    return node.i === end.i && node.j === end.j && node.k === end.k
  }
  const isEnd_nextTo = (node: IPathNode) => {
    // TODO: next to / below
    if (node.k !== end.k) {
      return false
    }
    let dx = Math.abs(node.i - end.i)
    let dy = Math.abs(node.j - end.j)
    if (dx + dy > 1) {
      return false
    }
    return true
  }

  let t0 = performance.now()
  let result = astarInterface.findPath<IPathNode>({
    start,
    isEnd: allowNextTo ? isEnd_nextTo : isEnd,
    neighbor,
    distance,
    heuristic,
    hash,
    timeout: 5000,
  })

  if (result.status === 'noPath') {
    result.path = [] // Clear path
  }

  let t1 = performance.now()
  console.log('path result', result, (t1 - t0).toFixed(1), 'ms')
  return result
}
